import { checkDate } from "common/helpers/Date";
import { feneTechManufacturerFromJSON, IFeneTechManufacturerData } from "common/records/FeneTechManufacturer";
import { IImageData } from "common/records/Image";
import { Nullable } from "common/types";
import { List, Map, MapOf, Record, fromJS } from "immutable";
import { DateStringNumber } from ".";

export const tokenFromJSON = (json: Partial<ITokenData>): TokenRecord => {
  const record: ITokenRecord = { ...(json as any) };

  record.data = fromJSWithDefaults(json.kind, json.data);

  if (json.payload) {
    record.payload = fromJS(json.payload);
  }

  if (json.images) {
    record.image_ids = List(json.images.map((i) => i.id));
  }

  if (json.created_at) {
    record.created_at = checkDate(json.created_at);
  }

  if (json.updated_at) {
    record.updated_at = checkDate(json.updated_at);
  }

  return new TokenRecord(record);
};

export const fromJSWithDefaults = (kind: Nullable<string>, data: any): Map<string, any> => {
  data = data || {};
  switch (kind) {
    case "salesforce":
      if (data.valid_types) {
        data.valid_types = data.valid_types.map((type: { value: string }) => {
          return type.value;
        });
      } else {
        data.valid_types = [jobBuilderTypes[0].id];
      }
      break;
    case "ingage":
      data.presentation_url = data.presentation_url || "";
      data.discount = data.discount || {
        name: "",
        description: "",
        amount: 0,
      };
      data.packages = data.packages || [
        {
          name: "",
          description: "",
          markup_factor: 0,
          markdown_factor: 0,
          default: true,
        },
      ];
      data.finance_options = data.finance_options || [
        {
          name: "",
          rate: 0,
          term: 0,
        },
      ];
      if (data.enabled === undefined) {
        data.enabled = true;
      }
      break;
    case "job_nimbus":
      if (data.jn_type === undefined) {
        data.jn_type = "job";
      }
      break;
    case "quick_measure":
      if (data.enabled === undefined) {
        data.enabled = true;
      }
      break;
    case "fene_tech":
      if (data.manufacturers) {
        data.manufacturers = List(
          data.manufacturers.map((manufacturer: IFeneTechManufacturerData) =>
            feneTechManufacturerFromJSON(manufacturer),
          ),
        );
      } else {
        data.manufacturers = List();
      }

      if (data.availableManufacturers) {
        data.availableManufacturers = List(
          data.availableManufacturers.map((manufacturer: IFeneTechManufacturerData) =>
            feneTechManufacturerFromJSON(manufacturer),
          ),
        );
      } else {
        data.availableManufacturers = List();
      }
      break;
    case "improveit360": {
      const cps = data.create_prospect_sale;
      if (cps === null || cps === undefined || cps === true) {
        data.create_prospect_sale = { appointment_resulted: true, signed_document_signed: true };
      } else if (cps === false) {
        data.create_prospect_sale = { appointment_resulted: false, signed_document_signed: false };
      } else {
        if (cps.appointment_resulted == null) {
          data.create_prospect_sale.appointment_resulted = true;
        }
        if (cps.signed_document_signed == null) {
          data.create_prospect_sale.signed_document_signed = true;
        }
      }
      if (data.create_marketing_opportunity == null) {
        data.create_marketing_opportunity = true;
      }
      break;
    }
    case "occ_pay":
      if (!data.payment_methods) {
        data.payment_methods = List(["credit_card", "ach"]);
      }
      break;
    default:
  }
  return fromJS(data);
};

export const TOKEN_KINDS = [
  "marketsharp",
  "eagleview",
  "salesforce",
  "job_nimbus",
  "hover",
  "improveit360",
  "sales_rabbit",
  "paysimple",
  "lead_perfection",
  "card_connect",
  "companycam",
  "jobprogress",
  "roof_scope",
  "accu_lynx",
  "fin_mkt",
  "plnar",
  "homevest_finance",
  "green_sky",
  "quick_measure",
  "ingage",
  "provia",
  "renoworks",
  "home_advisor",
  "angies_list",
  "houzz",
  "service_minder",
  "fene_tech",
  "occ_pay",
  "one_click_crm",
  "one_look",
] as const;

export type TokenKind = (typeof TOKEN_KINDS)[number];

export const paymentProviders: List<TokenKind> = List(["paysimple", "card_connect", "occ_pay"]);

export type JobObject = "lead" | "opportunity";
export const jobObjects: { id: JobObject; name: string }[] = [
  { id: "opportunity", name: "Opportunity" },
  { id: "lead", name: "Lead" },
];

export const jobBuilderTypes = [
  { id: "occ", name: "OCC" },
  { id: "csv", name: "CSV Import" },
  { id: "salesforce", name: "Salesforce" },
  { id: "companycam", name: "CompanyCam" },
  { id: "improveit360", name: "Improveit360" },
  { id: "job_nimbus", name: "JobNimbus" },
  { id: "jobprogress", name: "JobProgress" },
  { id: "lead_perfection", name: "LeadPerfection" },
  { id: "marketsharp", name: "MarketSharp" },
  { id: "sales_rabbit", name: "SalesRabbit" },
  { id: "creator", name: "Creator" },
  { id: "rsf_leads_api_lead", name: "Lead Conversion Callback" },
];

export const attachSignableDocOptions = ["sent", "signed"];
export const attachContractOptions = ["button", "email", "sent", "signed", "result_signed"];
export const attachEstimateOptions = ["button", "email"];

export type CrmFilter = MapOf<{ valid_values: Nullable<List<string>>; column: string }>;

export interface ITokenData {
  id: number;
  uuid: string;
  org_id: number;
  user_id: number;
  kind: TokenKind;
  images: IImageData[];
  data: any;
  payload: any;
  valid_token: boolean;
  created_at: DateStringNumber;
  updated_at: DateStringNumber;
  loading?: boolean;
}

export interface ITokenRecord {
  id: number;
  uuid: string;
  org_id: number;
  user_id: number;
  kind: TokenKind;
  image_ids: List<number>;
  data: Map<string, any>;
  payload: Map<string, any>;
  valid_token: boolean;
  created_at: Date;
  updated_at: Date;

  loading: boolean;
}

const defaultTokenProps: ITokenRecord = {
  id: 0,
  uuid: "",
  user_id: 0,
  org_id: 0,
  kind: "marketsharp",
  image_ids: List<number>(),
  data: Map(),
  payload: Map(),
  valid_token: false,
  created_at: new Date(),
  updated_at: new Date(),
  loading: false,
};

export class TokenRecord extends Record(defaultTokenProps) implements ITokenRecord {}
